import { UnderlyingSelectEnum, UnderlyingsEnum } from 'enums/underlying';
import { Field, useFormikContext } from 'formik';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Col, Row, Table } from 'react-bootstrap';
import {
  ADMISSIONS,
  CONSIDERATION_EQUAL_FIELD,
  CONSIDERATION_FROM_FIELD,
  CONSIDERATION_OFFERED_HELP,
  CONSIDERATION_TO_FIELD,
  COUNTRIES_MEMBER_STATE,
  CURRENCIES,
  DENOMINATION_PER_UNIT_CONDITION,
  DENOMINATION_PER_UNIT_FIELD,
  IDENTIFIER_OR_NAME_OF_UNDERLYING_CONDITION,
  MATURITY_DATE_FIELD,
  MATURITY_OR_EXPIRY_DATE_CONDITION,
  MEMBER_STATE_ADMISSION_TO_TRADING_FIELD,
  MEMBER_STATE_OFFER_TO_PUBLIC_FIELD,
  OFFER_END_DATE_FIELD,
  SECURITIES,
  TRADING_CHARACTERISTICS,
  TRADING_VENUE_CHARACTERISTICS_FIELD,
  UNDERLYINGS,
  UNDERLYING_HELP,
  UNDERLYING_IDENTIFIER_FIELD,
  UNDERLYING_NAME_OR_ISIN_FIELD,
  TradingCharacteristicType,
} from '../../../../constants';
import { Checkbox, Datepicker, Fieldset, Help, Input, Select } from '../../../form';
import { FinalTermType } from '../../app';
import { SecurityListType } from './list';
import { MultChoiceMultSelect } from '../../../form/multichoiceselect/multi-choice-multi-select';

export type SecurityFieldsType = {
  orderNumber: number;
  fisn: string;
  isin: string;
  cfiCode: string;
  bailInAbility?: boolean;
  underlying?: {
    identifier?: string;
    nameOrIsin?: string;
    select?: string;
  };
  type: string;
  offerEndDate?: Date;
  maturityDate?: Date;
  denominationPerUnit?: number;
  issuanceCurrency: string;
  volume?: {
    from?: number;
    to?: number;
    equal?: number;
  };
  price?: {
    from?: number;
    to?: number;
    equal?: number;
    pending?: boolean;
  };
  consideration?: {
    from?: number;
    to?: number;
    equal?: number;
    pending?: boolean;
  };
  typeOfOfferAdmission: string;
  offerToPublic?: boolean;
  memberStateAdmissionToTrading?: [];
  memberStateOfferToPublic?: [];
  tradingVenueCharacteristics?: string;
};

export type SecurityFieldProps = {
  controlId: string;
  index: number;
};

export const SecurityFields: React.FC<React.PropsWithChildren<SecurityFieldProps>> = ({ controlId, index }) => {
  const { values, setFieldValue } = useFormikContext<FinalTermType>();

  const lastUnderlyingSelectRef = useRef('');

  const showMemberStateOfferToPublic = useMemo(
    () => ((values as any)[controlId] as SecurityListType)[index].offerToPublic,
    [values, controlId, index]
  );

  const showMemberStateAdmissionToTrading = useMemo(
    () =>
      ['IRMT', 'IPTM', 'IMTF', 'SIOP', 'SIWO', 'SOOA', 'IORM'].includes(
        ((values as any)[controlId] as SecurityListType)[index].typeOfOfferAdmission
      ),
    [values, controlId, index]
  );

  const isMemberStateAdmissionToTradingRequired = useMemo(
    () => ['IRMT', 'IPTM', 'IMTF', 'SOOA'].includes(((values as any)[controlId] as SecurityListType)[index].typeOfOfferAdmission),
    [values, controlId, index]
  );

  const controlIdWithIndex = useMemo(() => `${controlId}[${index}]`, [controlId, index]);

  const security = useMemo(() => ((values as any)[controlId] as SecurityListType)[index], [values, controlId, index]);

  const underlyingSelect = useMemo(() => security.underlying?.select, [security]);

  const disableConsiderationOffered = useMemo(() => security.consideration?.pending, [security]);

  const disableDenominationPerUnit = useMemo(() => DENOMINATION_PER_UNIT_CONDITION.test(security.cfiCode), [security]);

  const disableUnderlying = useMemo(() => IDENTIFIER_OR_NAME_OF_UNDERLYING_CONDITION.test(security.cfiCode), [security]);

  const disableMaturityOrExpiryDate = useMemo(() => MATURITY_OR_EXPIRY_DATE_CONDITION.test(security.cfiCode), [security]);

  useEffect(() => {
    if (disableConsiderationOffered) {
      setFieldValue(`${controlIdWithIndex}${CONSIDERATION_EQUAL_FIELD}`, undefined, false);
      setFieldValue(`${controlIdWithIndex}${CONSIDERATION_FROM_FIELD}`, undefined, false);
      setFieldValue(`${controlIdWithIndex}${CONSIDERATION_TO_FIELD}`, undefined, false);
    }
  }, [setFieldValue, disableConsiderationOffered, controlIdWithIndex]);

  useEffect(() => {
    if (disableDenominationPerUnit) {
      setFieldValue(`${controlIdWithIndex}${DENOMINATION_PER_UNIT_FIELD}`, undefined, false);
    }
  }, [setFieldValue, disableDenominationPerUnit, controlIdWithIndex]);

  useEffect(() => {
    if (disableUnderlying) {
      setFieldValue(`${controlIdWithIndex}${UNDERLYING_IDENTIFIER_FIELD}`, undefined, false);
      setFieldValue(`${controlIdWithIndex}${UNDERLYING_NAME_OR_ISIN_FIELD}`, undefined, false);
    }
  }, [setFieldValue, disableUnderlying, controlIdWithIndex]);

  useEffect(() => {
    if (disableMaturityOrExpiryDate) {
      setFieldValue(`${controlIdWithIndex}${MATURITY_DATE_FIELD}`, undefined, false);
    }
  }, [setFieldValue, disableMaturityOrExpiryDate, controlIdWithIndex]);

  useEffect(() => {
    if (!showMemberStateAdmissionToTrading) {
      setFieldValue(`${controlIdWithIndex}${MEMBER_STATE_ADMISSION_TO_TRADING_FIELD}`, undefined, false);
      setFieldValue(`${controlIdWithIndex}${TRADING_VENUE_CHARACTERISTICS_FIELD}`, undefined, false);
    }
  }, [setFieldValue, showMemberStateAdmissionToTrading, controlIdWithIndex]);

  useEffect(() => {
    if (!showMemberStateOfferToPublic) {
      setFieldValue(`${controlIdWithIndex}${MEMBER_STATE_OFFER_TO_PUBLIC_FIELD}`, undefined, false);
      setFieldValue(`${controlIdWithIndex}${OFFER_END_DATE_FIELD}`, undefined, false);
    }
  }, [setFieldValue, showMemberStateOfferToPublic, controlIdWithIndex]);

  useEffect(() => {
    if (underlyingSelect === UnderlyingSelectEnum.basket) {
      setFieldValue(`${controlIdWithIndex}${UNDERLYING_IDENTIFIER_FIELD}`, UnderlyingsEnum.BSKT);
      setFieldValue(`${controlIdWithIndex}${UNDERLYING_NAME_OR_ISIN_FIELD}`, undefined, false);
    } else if (underlyingSelect === UnderlyingSelectEnum.identifier) {
      setFieldValue(`${controlIdWithIndex}${UNDERLYING_NAME_OR_ISIN_FIELD}`, undefined, false);
    } else if (underlyingSelect === UnderlyingSelectEnum.isin || underlyingSelect === UnderlyingSelectEnum.name) {
      if (
        (lastUnderlyingSelectRef.current === UnderlyingSelectEnum.isin || lastUnderlyingSelectRef.current === UnderlyingSelectEnum.name) &&
        lastUnderlyingSelectRef.current !== underlyingSelect
      ) {
        setFieldValue(`${controlIdWithIndex}${UNDERLYING_NAME_OR_ISIN_FIELD}`, undefined, false);
      }

      setFieldValue(`${controlIdWithIndex}${UNDERLYING_IDENTIFIER_FIELD}`, undefined, false);
    } else {
      setFieldValue(`${controlIdWithIndex}${UNDERLYING_IDENTIFIER_FIELD}`, undefined, false);
      setFieldValue(`${controlIdWithIndex}${UNDERLYING_NAME_OR_ISIN_FIELD}`, undefined, false);
    }

    if (underlyingSelect) {
      lastUnderlyingSelectRef.current = underlyingSelect;
    }
  }, [setFieldValue, controlIdWithIndex, underlyingSelect]);

  const computedUnderlying = useMemo(
    () =>
      UNDERLYINGS.filter((underlying) => underlying.id !== UnderlyingsEnum.BSKT).map((underlying) => ({
        value: underlying.id,
        label: underlying.name,
        orderIndex: underlying.orderIndex,
      })),
    []
  );

  const computedUnderlyingSelect = useMemo(
    () =>
      [UnderlyingSelectEnum.basket, UnderlyingSelectEnum.identifier, UnderlyingSelectEnum.isin, UnderlyingSelectEnum.name].map((value) => ({
        value,
        label: value,
      })),
    []
  );

  const computedSecurities = useMemo(
    () =>
      SECURITIES.map((security) => ({
        value: security.id,
        label: security.name,
        orderIndex: security.orderIndex,
      })),
    []
  );

  const computedCurrencies = useMemo(
    () =>
      CURRENCIES.map((currency) => ({
        value: currency.id,
        label: currency.id,
      })),
    []
  );

  const computedAdmissions = useMemo(
    () =>
      ADMISSIONS.map((admission) => ({
        value: admission.id,
        label: admission.name,
        disabled: admission.disabled,
      })),
    []
  );

  const computedCountriesMemberState = useMemo(
    () =>
      COUNTRIES_MEMBER_STATE.map((country) => ({
        id: country.id,
        name: country.name,
      })),
    []
  );

  const isCharacteristicDisabled = useCallback(
    (characteristic: TradingCharacteristicType) => {
      if (['RMKT', 'RMQI'].includes(characteristic.id) && ['IMTF', 'SIOP'].includes(security.typeOfOfferAdmission)) {
        return true;
      }
      return (
        ['MLTF', 'MSGM'].includes(characteristic.id) && ['IRMT', 'IPTM', 'SIWO', 'SOOA', 'IORM'].includes(security.typeOfOfferAdmission)
      );
    },
    [security]
  );

  const computedTradingCharacteristics = useMemo(
    () =>
      TRADING_CHARACTERISTICS.map((tradingCharacteristic) => ({
        value: tradingCharacteristic.id,
        label: tradingCharacteristic.name,
        orderIndex: tradingCharacteristic.orderIndex,
        disabled: isCharacteristicDisabled(tradingCharacteristic),
      })),
    [isCharacteristicDisabled]
  );

  const underlyingField = useMemo(
    () => (
      <>
        {underlyingSelect === UnderlyingSelectEnum.identifier && (
          <Select options={computedUnderlying} label="Identifier" controlId={`${controlIdWithIndex}.underlying.identifier`} />
        )}
        {underlyingSelect === UnderlyingSelectEnum.name && (
          <Input
            type="text"
            label="Name"
            controlId={`${controlIdWithIndex}.underlying.nameOrIsin`}
            additionalAttributes={{ maxLength: 50 }}
          />
        )}
        {underlyingSelect === UnderlyingSelectEnum.isin && (
          <Input type="text" label="ISIN" controlId={`${controlIdWithIndex}.underlying.nameOrIsin`} />
        )}
      </>
    ),
    [computedUnderlying, controlIdWithIndex, underlyingSelect]
  );

  return (
    <Row className="security-fields">
      <Col>
        <Row>
          <Col>
            <Fieldset>
              <Row>
                <Col sm={12} md={6} lg={4}>
                  <Input
                    type="text"
                    label="Financial Instrument Short Name(FISN)"
                    controlId={`${controlIdWithIndex}.fisn`}
                    required={true}
                    additionalAttributes={{ maxLength: 35 }}
                  />
                </Col>
                <Col sm={12} md={6} lg={4}>
                  <Input
                    type="text"
                    label="ISIN"
                    controlId={`${controlIdWithIndex}.isin`}
                    required={true}
                    additionalAttributes={{ maxLength: 12 }}
                  />
                </Col>
                <Col xs={6} lg={2}>
                  <Input
                    type="text"
                    label="CFI Code"
                    controlId={`${controlIdWithIndex}.cfiCode`}
                    required={true}
                    additionalAttributes={{ maxLength: 6 }}
                  />
                </Col>
                <Col xs={6} lg={2}>
                  <Checkbox label="Bail-in-ability" controlId={`${controlIdWithIndex}.bailInAbility`} />
                </Col>
              </Row>
            </Fieldset>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xs={12} lg={6}>
            <Fieldset>
              <Row>
                <Col className="d-flex justify-content-center">
                  <span>Underlying</span>
                  <Help placement="top">{UNDERLYING_HELP}</Help>
                </Col>
              </Row>
              <Row>
                <Col xs={6}>
                  <Select
                    options={computedUnderlyingSelect}
                    label="Type"
                    controlId={`${controlIdWithIndex}.underlying.select`}
                    showEmptyOption={true}
                  />
                </Col>
                <Col>{underlyingField}</Col>
              </Row>
            </Fieldset>
          </Col>
          <Col xs={12} lg={6}>
            <Fieldset className="h-100">
              <Row className="h-100 align-items-end">
                <Col xs={6}>
                  <Select options={computedSecurities} label="Type of security" controlId={`${controlIdWithIndex}.type`} required={true} />
                </Col>
                <Col>
                  <Datepicker
                    label="Maturity/expiry date"
                    help="In case of perpetual debt securities, enter the value 9999/12/31"
                    disabled={disableMaturityOrExpiryDate}
                    controlId={`${controlIdWithIndex}.maturityDate`}
                  />
                </Col>
              </Row>
            </Fieldset>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            <Fieldset>
              <Row>
                <Col xs={12} lg={2}>
                  <Row>
                    <Col xs={6} lg={12} className="mb-4">
                      <Input
                        type="big-number"
                        label="Denomination per unit"
                        disabled={disableDenominationPerUnit}
                        controlId={`${controlIdWithIndex}.denominationPerUnit`}
                        additionalAttributes={{ step: 0.00001 }}
                      />
                    </Col>
                    <Col xs={6} lg={12}>
                      <Select
                        options={computedCurrencies}
                        label="Issuance currency"
                        controlId={`${controlIdWithIndex}.issuanceCurrency`}
                        required={true}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col xs={12} lg={10}>
                  <Table responsive borderless>
                    <thead>
                      <tr>
                        <th />
                        <th className="text-center">From</th>
                        <th className="text-center">To</th>
                        <th className="text-center">Equal</th>
                        <th className="text-center">Pending</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="align-middle">
                          <Row>
                            <Col className="d-flex justify-content-end text-end">
                              <span>Consideration offered</span>
                              <Help placement="top">{CONSIDERATION_OFFERED_HELP}</Help>
                            </Col>
                          </Row>
                        </td>
                        <td>
                          <Input
                            type="big-number"
                            controlId={`${controlIdWithIndex}${CONSIDERATION_FROM_FIELD}`}
                            disabled={disableConsiderationOffered}
                          />
                        </td>
                        <td>
                          <Input
                            type="big-number"
                            controlId={`${controlIdWithIndex}${CONSIDERATION_TO_FIELD}`}
                            disabled={disableConsiderationOffered}
                          />
                        </td>
                        <td>
                          <Input
                            type="big-number"
                            controlId={`${controlIdWithIndex}${CONSIDERATION_EQUAL_FIELD}`}
                            disabled={disableConsiderationOffered}
                          />
                        </td>
                        <td>
                          <Checkbox centered controlId={`${controlIdWithIndex}.consideration.pending`} />
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </Fieldset>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            <Fieldset>
              <Row>
                <Col xs={12} lg={4} className="d-flex align-items-end">
                  <Select
                    options={computedAdmissions}
                    label="Type of offer / admission"
                    controlId={`${controlIdWithIndex}.typeOfOfferAdmission`}
                    required={true}
                  />
                </Col>
                {showMemberStateAdmissionToTrading && (
                  <>
                    <Col xs={12} lg={4} className="d-flex align-items-end">
                      <Field
                        name={`${controlIdWithIndex}${MEMBER_STATE_ADMISSION_TO_TRADING_FIELD}`}
                        children={(p: any) => {
                          return (
                            <MultChoiceMultSelect
                              choices={computedCountriesMemberState}
                              question="Member state(s) in which an admission to trading is contemplated"
                              required={isMemberStateAdmissionToTradingRequired}
                              {...p}
                            />
                          );
                        }}
                      />
                    </Col>
                    <Col xs={12} lg={4} className="d-flex align-items-end">
                      <Select
                        options={computedTradingCharacteristics}
                        label="Characteristics of the trading venue"
                        controlId={`${controlIdWithIndex}${TRADING_VENUE_CHARACTERISTICS_FIELD}`}
                        required={true}
                      />
                    </Col>
                  </>
                )}
              </Row>
              <Row>
                <Col xs={12} lg={4} className="d-flex align-items-end">
                  <Checkbox
                    label="Offer to the public"
                    help="Select in case the securities are offered to the public"
                    controlId={`${controlIdWithIndex}.offerToPublic`}
                  />
                </Col>
                {showMemberStateOfferToPublic && (
                  <>
                    <Col xs={12} lg={4} className="d-flex align-items-end">
                      <Field
                        name={`${controlIdWithIndex}${MEMBER_STATE_OFFER_TO_PUBLIC_FIELD}`}
                        children={(p: any) => {
                          return (
                            <MultChoiceMultSelect
                              choices={computedCountriesMemberState}
                              question="Member state(s) in which an offer to public is contemplated"
                              required={true}
                              name={`${controlIdWithIndex}${MEMBER_STATE_OFFER_TO_PUBLIC_FIELD}`}
                              {...p}
                            />
                          );
                        }}
                      />
                    </Col>

                    <Col xs={12} lg={4} className="d-flex align-items-end">
                      <Datepicker
                        label="Offer end date"
                        help="In case of a perpetual offer, enter the value 9999/12/31"
                        controlId={`${controlIdWithIndex}.offerEndDate`}
                        required={true}
                      />
                    </Col>
                  </>
                )}
              </Row>
            </Fieldset>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
